import React from 'react';
import PropTypes from 'prop-types';

/**
 * TextArea Component
 * 
 * Standardized multiline text input component for forms.
 * Supports various sizes, states, and optional character count.
 * 
 * @example
 * // Basic textarea
 * <TextArea
 *   value={feedback}
 *   onChange={(e) => setFeedback(e.target.value)}
 *   placeholder="Enter your feedback"
 *   rows={4}
 * />
 * 
 * @example
 * // Textarea with character count
 * <TextArea
 *   value={bio}
 *   onChange={(e) => setBio(e.target.value)}
 *   maxLength={500}
 *   showCharCount
 * />
 */
const TextArea = ({
  // Value and change handlers
  value = '',
  onChange,
  onBlur,
  onFocus,
  
  // Textarea attributes
  name,
  id,
  placeholder,
  disabled = false,
  readOnly = false,
  required = false,
  autoFocus = false,
  maxLength,
  rows = 4,
  cols,
  
  // Resize control
  resize = 'vertical', // 'none' | 'vertical' | 'horizontal' | 'both'
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  fullWidth = true,
  
  // States
  error = '',
  success = '',
  helperText = '',
  
  // Character count
  showCharCount = false,
  
  // Styling
  className = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  
  // Additional props
  ...rest
}) => {
  // Determine state
  const hasError = Boolean(error);
  const hasSuccess = Boolean(success) && !hasError;
  const hasHelperText = Boolean(helperText) && !hasError && !hasSuccess;
  
  // Auto-generate IDs for aria-describedby
  const errorId = id ? `${id}-error` : undefined;
  const successId = id ? `${id}-success` : undefined;
  const helperId = id ? `${id}-helper` : undefined;
  const charCountId = id ? `${id}-charcount` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (hasError && errorId) describedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
  if (hasSuccess && successId) describedBy = describedBy ? `${describedBy} ${successId}` : successId;
  if (hasHelperText && helperId) describedBy = describedBy ? `${describedBy} ${helperId}` : helperId;
  if (showCharCount && charCountId) describedBy = describedBy ? `${describedBy} ${charCountId}` : charCountId;
  
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-base',
    lg: 'px-4 py-2.5 text-lg',
  };
  
  // Resize classes
  const resizeClasses = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize',
  };
  
  // State classes
  const baseClasses = 'border rounded-md shadow-sm transition-colors duration-200 focus:outline-none bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm';
  
  let stateClasses = '';
  if (disabled) {
    stateClasses = 'bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-dark-400 border-gray-300 dark:border-dark-700 cursor-not-allowed';
  } else if (readOnly) {
    stateClasses = 'bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-dark-400 border-gray-300 dark:border-dark-700 cursor-not-allowed';
  } else if (hasError) {
    stateClasses = 'border-red-300 dark:border-red-700 text-gray-900 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-400';
  } else if (hasSuccess) {
    stateClasses = 'border-green-300 dark:border-green-700 text-gray-900 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-green-500 dark:focus:border-green-400';
  } else {
    stateClasses = 'border-gray-300 dark:border-dark-600 text-gray-900 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400';
  }
  
  // Width class
  const widthClass = fullWidth ? 'w-full' : '';
  
  // Combine all classes
  const textareaClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    widthClass,
    resizeClasses[resize],
    className,
  ].filter(Boolean).join(' ');
  
  // Character count
  const charCount = value.length;
  const isNearLimit = maxLength && charCount > maxLength * 0.9;
  const isOverLimit = maxLength && charCount > maxLength;
  
  return (
    <div className={fullWidth ? 'w-full' : 'inline-block'}>
      {/* Textarea Element */}
      <textarea
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoFocus={autoFocus}
        maxLength={maxLength}
        rows={rows}
        cols={cols}
        className={textareaClasses}
        aria-label={ariaLabel}
        aria-describedby={describedBy || undefined}
        aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError}
        {...rest}
      />
      
      {/* Character Count */}
      {showCharCount && maxLength && (
        <div 
          id={charCountId}
          className={`mt-1 text-xs text-right ${
            isOverLimit ? 'text-red-600' : 
            isNearLimit ? 'text-yellow-600' : 
            'text-gray-500'
          }`}
        >
          {charCount} / {maxLength}
        </div>
      )}
      
      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
      
      {/* Success Message */}
      {hasSuccess && (
        <p id={successId} className="mt-1 text-xs text-green-600">
          {success}
        </p>
      )}
      
      {/* Helper Text */}
      {hasHelperText && (
        <p id={helperId} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      )}
    </div>
  );
};

TextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  name: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  rows: PropTypes.number,
  cols: PropTypes.number,
  
  resize: PropTypes.oneOf(['none', 'vertical', 'horizontal', 'both']),
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  
  error: PropTypes.string,
  success: PropTypes.string,
  helperText: PropTypes.string,
  
  showCharCount: PropTypes.bool,
  
  className: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default TextArea;
